/**
 * Synthesize a terse user-facing summary when the assistant emitted no
 * visible text but its tool work succeeded.
 *
 * Three-layer recovery (see commit message for fix/pages-3 / commit 4):
 *   Layer 1 — look at what the tools actually did (update_module,
 *             create_module, html.patch directives) and write a short
 *             sentence describing that outcome.
 *   Layer 2 — idle watchdog: handled in useChat.ts (timer-based UI hint).
 *   Layer 3 — true empty response (no chunks, no tool work): handled in
 *             useChat.ts as a transient retry bubble, never persisted.
 *
 * Inputs are loose — both parseSSEText (the non-streaming fallback in
 * apps/web/src/lib/api/endpoints/chat.ts) and useChat.ts's live-stream
 * finalize path feed us whatever tool metadata they have at the moment
 * of fallback. We accept unknowns and shape-check narrowly so a single
 * helper serves both call sites.
 */

type ToolCall = { name?: string; params?: unknown }
type ToolResult = {
  type?: string
  tool?: string
  toolName?: string
  ok?: boolean
  success?: boolean
  receiptType?: string
  [key: string]: unknown
}
type UiAction = {
  type?: string
  sectionId?: string
  label?: string
  [key: string]: unknown
}

/**
 * Return a short one-sentence summary, or `null` if we can't make one
 * from the available tool/directive evidence. Callers treat `null` as
 * "we don't know — let higher-level logic decide" (which for live stream
 * means suppress the bubble, and for the non-streaming fallback means
 * return empty text so the caller can layer its own UI on top).
 */
export function synthesizeToolSummary(
  toolCalls: ToolCall[] | undefined,
  toolResults: ToolResult[] | undefined,
  uiActions: UiAction[] | undefined,
): string | null {
  const calls = Array.isArray(toolCalls) ? toolCalls : []
  const results = Array.isArray(toolResults) ? toolResults : []
  const actions = Array.isArray(uiActions) ? uiActions : []

  const resultOk = (r: ToolResult): boolean => {
    if (r.type === 'tool_error' || r.receiptType === 'error') return false
    if (typeof r.ok === 'boolean') return r.ok
    if (typeof r.success === 'boolean') return r.success
    // Absent explicit failure signal: treat `tool_result`/`tool_receipt`
    // as success (matches the way useChat.ts increments toolsUsedCount).
    return r.type !== 'tool_error'
  }
  const resultTool = (r: ToolResult): string =>
    String(r.tool ?? r.toolName ?? '')

  // ---- Layer 1a: create_module for an html_viewer (first-turn build) ----
  // When the agent created a new html_viewer module, the create_module
  // call carries `moduleType: 'html_viewer'` and often a `title` in config.
  for (const call of calls) {
    if (call.name !== 'create_module') continue
    const params = (call.params ?? {}) as Record<string, unknown>
    const moduleType = typeof params.moduleType === 'string' ? params.moduleType : ''
    if (moduleType !== 'html_viewer') continue
    // Require a matching successful receipt.
    const ok = results.some((r) => resultOk(r) && resultTool(r) === 'create_module')
    if (!ok) continue
    const config = (params.config ?? {}) as Record<string, unknown>
    const title = typeof config.title === 'string' ? config.title.trim() : ''
    const label = typeof params.label === 'string' ? params.label.trim() : ''
    const name = title || label
    return name ? `Built ${name}.` : 'Built the page.'
  }

  // ---- Layer 1b: update_module for an html config (edit turn) ----
  for (const call of calls) {
    if (call.name !== 'update_module') continue
    const ok = results.some((r) => resultOk(r) && resultTool(r) === 'update_module')
    if (!ok) continue
    return 'Updated the page.'
  }

  // ---- Layer 1c: html.* directives streamed as UI actions ----
  // Patch/insert/remove directives mean the live stream successfully
  // authored section edits on the active page. Gather distinct section
  // labels (falling back to sectionId) so we can name them.
  const htmlEditActions = actions.filter(
    (a) =>
      a.type === 'html.patch' ||
      a.type === 'html.insert' ||
      a.type === 'html.remove',
  )
  if (htmlEditActions.length > 0) {
    const sections: string[] = []
    const seen = new Set<string>()
    for (const a of htmlEditActions) {
      const raw = typeof a.label === 'string' && a.label.trim()
        ? a.label.trim()
        : typeof a.sectionId === 'string' && a.sectionId.trim()
          ? a.sectionId.trim()
          : ''
      if (!raw) continue
      const pretty = raw.replace(/[-_]+/g, ' ').trim()
      const key = pretty.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      sections.push(pretty)
      if (sections.length >= 3) break
    }
    if (sections.length > 0) {
      const list =
        sections.length === 1
          ? sections[0]
          : sections.length === 2
            ? `${sections[0]} and ${sections[1]}`
            : `${sections.slice(0, -1).join(', ')}, and ${sections[sections.length - 1]}`
      return `Updated ${list}.`
    }
    return 'Updated the page.'
  }

  // ---- Layer 1d: html.complete — full page build finished on the stream ----
  if (actions.some((a) => a.type === 'html.complete')) {
    // Prefer the init/complete title if present.
    const init = actions.find((a) => a.type === 'html.init')
    const title =
      (init && typeof init.label === 'string' && init.label.trim()) ||
      (init && typeof (init as Record<string, unknown>).title === 'string' &&
        String((init as Record<string, unknown>).title).trim()) ||
      ''
    return title ? `Built ${title}.` : 'Built the page.'
  }

  // No tool/directive evidence we know how to summarise.
  return null
}
